"use client";

import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useEffect } from "react";
import {
  useAccount,
  useBalance,
  useBlockNumber,
  useSwitchChain,
} from "wagmi";
import { Button, type ButtonProps } from "./button/button";
import toast from "react-hot-toast";
import { isMainnet } from "@/constants/flags.constant";
import { blessnetMainnet, blessnetSepolia } from "@/config/bless-chain";

/**
 *
 * Renders a button that is aware of the connection state,
 * so it falls back to connecting as follows:
 *
 * If the user is not connected, renders a button to connect
 * If the user is connected to the wrong network, display a switch network button
 * Else render a button with the props passed down
 *
 * @params bypass If true render the button independently of the connection status
 * @params disconnectedText The text to display when not connected
 *
 * @example
 *
 * <Web3Button
 *   bypass={iDontCareAboutTheBalanceOrMetamaskStatus}
 *   onClick={handleClick}
 *   disconnectedText="Connect To mint"
 * >
 *  Lick Me
 * </Web3Button>
 */
export const Web3Button = ({
  bypass = false,
  bypassSwitchNetwork = false,
  disconnectedText = "Connect wallet",
  chainId = isMainnet ? blessnetMainnet.id : blessnetSepolia.id,
  onClick,
  ...props
}: ButtonProps & {
  bypass?: boolean;
  bypassSwitchNetwork?: boolean;
  chainId?: number;
  disconnectedText?: string;
}) => {

  const { isConnected, isConnecting, address } = useAccount();
  // returns no chain if not connected
  const { chain } = useAccount();
  const {
    chains,
    switchChainAsync,
    isPending: isLoading,
    isIdle,
    ...r
  } = useSwitchChain();
  const { data: blockNumber } = useBlockNumber({ chainId, watch: true });

  const balance = useBalance({
    address,
    chainId,
  });
  const { refetch } = balance;
  useEffect(() => {
    refetch();
  }, [blockNumber, refetch]);

  if (bypass) {
    return <Button {...props} />;
  }
  if (isConnected && chain?.id !== chainId && !bypassSwitchNetwork) {
    const targetChain = chains.find((chain) => chain.id === chainId)!;
    console.log(chains, targetChain, chainId);
    return (
      <Button
        {...props}
        disabled={!isIdle}
        onClick={(evt) => {
          switchChainAsync({ chainId: targetChain.id });
          evt.preventDefault();
          evt.stopPropagation();
        }}
        type="button"
      >
        {isLoading
          ? `Switching to ${targetChain?.name}...`
          : `Switch to ${targetChain?.name || "unknown"}`}
      </Button>
    );
  }

  if (!isConnected) {
    return (
      <ConnectButton.Custom>
        {({ account, chain, openConnectModal }) => (
          <Button
            loading={isConnecting}
            onClick={(evt) => {
              evt.preventDefault();
              openConnectModal();
            }}
            className={props.className}
            {...props}
            disabled={isConnecting}
          >
            {isConnecting ? "Connecting..." : disconnectedText}
          </Button>
        )}
      </ConnectButton.Custom>
    );
  }

  if (false && balance.data?.value === BigInt(0)) {
    return (
      <Button
        {...props}
        onClick={(evt) => {
          evt.preventDefault();
          toast.error(
            "You don't have enough ETH in your wallet to perform this transaction.",
          );
        }}
      >
        {props.children}
      </Button>
    );
  }

  const { children, ...rest } = props;

  return (
    <Button onClick={onClick} {...rest}>
      {children}
    </Button>
  );
};
